import { useParams } from "react-router-dom";
import { useContext, useEffect, useState, useCallback } from "react";
import ProductApi from "../../api/product";
import ProductTypeApi from "../../api/product-type";
import PurchaseTicketApi from "../../api/purchase-ticket";
import CounterProductApi from "../../api/counter-product";
import { getCopyOf, localizeNumbers } from "../../functions";
import ShoppingCardWarning from "../../pages/purchase/warning";
import ProductTypeSpecs, {
  ProductTypeSpec,
  SpecDisplay,
} from "../../types/prtoduct-type-specs";
import AuthContext from "../auth-context";
import SearchFilters from "../search-filters";
import FilterWrapper from "../search-filters/filter-wrapper";
import SortBox from "../sort-box";
import Col from "../tailwind/col";
import Row from "../tailwind/row";
import ProductItem from "./item";
import ProductSuggest from "./suggest";
import Product from "../../types/product";

export interface IProductListProps {
  onSuccess: (ticketId: string) => void;
}

export default function ProductList({ onSuccess }: IProductListProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [conterProducts, setConterProducts] = useState<Product[]>([]);
  const [productSpecs, setProductSpecs] = useState<ProductTypeSpecs>({});
  const [sortBy, setSortBy] = useState<string>("newest");
  const [showCounter, setShowCounter] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [mainModemProduct, setMainModemProduct] = useState<Product>();
  const [modemProduct, setModemProduct] = useState<Product>();
  const [hasModem, setHasModem] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [shouldFinalize, setShouldFinalize] = useState(false);
  const [snappPayOnly, setSnappPayOnly] = useState(false);

  const context = useContext(AuthContext);
  const { activeFileId } = context;
  const { type } = useParams();

  let productType = 0;
  switch (type) {
    case "service":
      productType = 0;
      break;
    case "packages":
      productType = 4;
      break;
    case "ip":
      productType = 6;
      break;
  }

  const onFinalSelected = useCallback(
    (finalProducts: number[]) => {
      new PurchaseTicketApi(context)
        .post(
          activeFileId!,
          productType,
          hasModem ? mainModemProduct?.identifier ?? 1 : finalProducts[0],
          hasModem ? finalProducts : finalProducts.slice(1)
        )
        .then((ticket) => {
          if (ticket.errorMessage) {
            console.error("Ticket error:", ticket.errorMessage);
          } else {
            new PurchaseTicketApi(context)
              .getAll()
              .then(context.setTickets)
              .catch(() => {});
            onSuccess(ticket.id);
          }
        });
    },
    [
      context,
      activeFileId,
      productType,
      hasModem,
      mainModemProduct?.identifier,
      onSuccess,
    ]
  );

  useEffect(() => {
    if (shouldFinalize && !showCounter && selectedProducts.length > 0) {
      onFinalSelected(selectedProducts);
      setShouldFinalize(false);
    }
    // eslint-disable-next-line
  }, [shouldFinalize, showCounter, selectedProducts]);

  useEffect(() => {
    if (activeFileId) {
      setLoading(true);

      const productApi = new ProductApi(context);
      const productTypeApi = new ProductTypeApi(context);

      productApi.getAll(activeFileId, productType).then((result) => {
        setProducts(result);
      });

      productTypeApi
        .get(productType, activeFileId)
        .then((result) => result && setProductSpecs(result.specs))
        .catch((error) => console.error("Error fetching data:", error))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line
  }, [activeFileId, productType]);

  const handleProductSelection = (productId: number, result: Product[]) => {
    const product = products.find((p) => p.id === productId);
    if (result.length !== 0) {
      if (
        product?.specs?.some(
          (spec) => spec.specId === 10 && spec.value !== null
        )
      ) {
        setShowCounter(true);
        setModemState(result);
      } else {
        addProductToSelected(productId);
      }
      setConterProducts(result);
    } else {
      addProductToSelected(productId);
    }
  };

  const setModemState = (result: Product[]) => {
    setMainModemProduct(result[0]);
    setModemProduct(result[1]);
    setHasModem(true);
    updateSelectedProducts(result[1].identifier);
  };

  const addProductToSelected = (productId: number) => {
    setHasModem(false);
    updateSelectedProducts(productId);
  };

  const updateSelectedProducts = (productId: number) => {
    setSelectedProducts((prevSelected) => {
      if (!prevSelected.includes(productId)) {
        const newSelectedProducts = [...prevSelected, productId];
        setShouldFinalize(true);
        return newSelectedProducts;
      }
      setShouldFinalize(true);
      return prevSelected;
    });
  };

  const productSelected = (productId: number): void => {
    new CounterProductApi(context)
      .get(productId)
      .then((result) => handleProductSelection(productId, result))
      .catch((error) => {
        console.error("Error fetching product details:", error);
      });
  };

  const selectedValuesChanges = (specId: string, data: string[]) => {
    var specs = getCopyOf(productSpecs);
    specs[specId].selectedValues = data ? [...data] : null;
    setProductSpecs(specs);
  };

  const selectedRangeChanges = (specId: string, data: number[]) => {
    var specs = getCopyOf(productSpecs);
    specs[specId].selectedRange = data ? [...data] : null;
    setProductSpecs(specs);
  };

  const isInRangeOfSpec = (spec: ProductTypeSpec, value: number) => {
    if (spec) {
      const { selectedRange } = spec;
      if (selectedRange) {
        return selectedRange[0] <= value && selectedRange[1] >= value;
      }
    }
    return true;
  };

  const getSortedFilteredProducts = () => {
    if (!products) return [];
    return sortProducts(filterProducts(products));
  };

  const sortProducts = (products: Product[]) => {
    return products.sort(compareProducts);
  };

  const compareProducts = (a: Product, b: Product) => {
    switch (sortBy) {
      case "newest":
        return b.id - a.id;
      case "grandest":
        return b.finalPrice - a.finalPrice;
      case "cheapest":
        return a.finalPrice - b.finalPrice;
      default:
        return 0;
    }
  };

  const filterProducts = (products: Product[]) => {
    return products.filter((product) => {
      if (snappPayOnly && !product.hasSnappPay) return false;
      if (!isInRangeOfSpec(productSpecs[255], product.finalPrice)) return false;

      return product.specs.every((spec) => {
        if (!(spec.specId in productSpecs)) return false;
        const { selectedValues } = productSpecs[spec.specId];
        switch (productSpecs[spec.specId].display) {
          case SpecDisplay.CheckBox:
          case SpecDisplay.DropDown:
          case SpecDisplay.RadioButton:
            if (!selectedValues || selectedValues.length === 0) return true;
            return selectedValues.includes(
              `${spec.value == null ? "" : spec.value}`
            );
          case SpecDisplay.Search:
            if (!selectedValues || selectedValues.length === 0) return true;
            var value = spec.value == null ? "" : `${spec.value}`;
            return localizeNumbers(value).includes(
              localizeNumbers(selectedValues[0])
            );
          case SpecDisplay.Slider:
            return isInRangeOfSpec(productSpecs[spec.specId], spec.value);
          default:
            return true;
        }
      });
    });
  };

  const handleSort = (value: string) => {
    setSortBy(value);
  };

  const filteredProducts = conterProducts.filter(
    (product) =>
      product.identifier !== (modemProduct?.identifier ?? 0) &&
      product.identifier !== (mainModemProduct?.identifier ?? 0)
  );

  const specs = [];
  for (const specId in productSpecs) {
    specs.push(
      <FilterWrapper
        key={specId}
        spec={productSpecs[specId]}
        selectedRangeChanges={(r) => selectedRangeChanges(specId, r)}
        selectedValuesChanges={(v) => selectedValuesChanges(specId, v)}
      />
    );
  }

  return (
    <>
      <ShoppingCardWarning />
      <Row>
        <SearchFilters
          snappPayOnly={snappPayOnly}
          setSnappPayOnly={setSnappPayOnly}
          className="col-12 col-lg-3"
        >
          {specs}
        </SearchFilters>

        <Col className="col-lg-9 pb-3">
          <SortBox onChange={handleSort} value={sortBy} />

          <Row className="row d-flex justify-content-center mx-1 mx-lg-0">
            {loading ? (
              <div className="text-center w-100 py-5">
                <div className="w-16 h-16 border-4 border-solid border-gray-300 border-t-4 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="mt-3">در حال بارگذاری محصولات...</p>
              </div>
            ) : (
              getSortedFilteredProducts().map((product) => (
                <ProductItem
                  key={product.id}
                  title={product.title}
                  finalPrice={product.finalPrice}
                  mainPrice={product.mainPrice}
                  packageTitle={product.packageTitle}
                  desc={product.desc}
                  Specs={productSpecs}
                  productSpecs={product.specs}
                  hasSnappPay={product.hasSnappPay}
                  onSelect={() => {
                    productSelected(product.id);
                  }}
                  productType={productType}
                />
              ))
            )}
            <ProductSuggest
              show={showCounter}
              mainProducts={
                hasModem
                  ? [mainModemProduct!]
                  : products.filter((p) => p.id === selectedProducts[0])
              }
              optionalProducts={hasModem ? [modemProduct!] : []}
              relatedProducts={filteredProducts}
              onCancel={() => setShowCounter(false)}
              onSubmit={onFinalSelected}
            />
          </Row>
        </Col>
      </Row>
    </>
  );
}
